import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Layouts
import { PublicLayout, AdminLayout } from './components/common';

// Route guards
import { ProtectedRoute, AdminRoute } from './components/auth';

// Public pages
import Home from './pages/public/Home';
import PublicLogin from './pages/public/Login';
import Register from './pages/public/Register';
import ForgotPassword from './pages/public/ForgotPassword';
import ResetPassword from './pages/public/ResetPassword';
import VerifyEmail from './pages/public/VerifyEmail';
import VerifyEmailSent from './pages/public/VerifyEmailSent';
import ReviewDetail from './pages/public/ReviewDetail';
import ProductDetail from './pages/public/ProductDetail';
import Search from './pages/public/Search';
import UserProfile from './pages/public/UserProfile';
import Settings from './pages/public/Settings';
import BlockedUsers from './pages/public/BlockedUsers';
import ChangePassword from './pages/public/ChangePassword';
import DeleteAccount from './pages/public/DeleteAccount';
import EditReview from './pages/public/EditReview';
import CreateReview from './pages/public/CreateReview';
import Notifications from './pages/public/Notifications';
import PrivacyPolicy from './pages/public/PrivacyPolicy';
import TermsOfService from './pages/public/TermsOfService';
import SavedReviews from './pages/public/SavedReviews';
import Categories from './pages/public/Categories';
import CategoryPage from './pages/public/CategoryPage';
import TagFeed from './pages/public/TagFeed';
import TagsBrowse from './pages/public/TagsBrowse';

// Admin pages (Mantine)
import {
  AdminDashboard,
  AdminUsers,
  AdminProducts,
  AdminManageProduct,
  AdminReviews,
  AdminModeration,
  AdminCategories,
  AdminImportProducts,
  AdminSeed,
} from './pages/admin';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          {/* Public routes with public layout */}
          <Route element={<PublicLayout />}>
            <Route index element={<Home />} />
            <Route path="login" element={<PublicLogin />} />
            <Route path="register" element={<Register />} />
            <Route path="forgot-password" element={<ForgotPassword />} />
            <Route path="reset-password" element={<ResetPassword />} />
            <Route path="verify-email" element={<VerifyEmail />} />
            <Route path="verify-email-sent" element={<VerifyEmailSent />} />

            {/* Public detail pages */}
            <Route path="products/:id" element={<ProductDetail />} />
            <Route path="reviews/:id" element={<ReviewDetail />} />
            <Route path="users/:username" element={<UserProfile />} />
            <Route path="search" element={<Search />} />
            <Route path="categories" element={<Categories />} />
            <Route path="categories/:category" element={<CategoryPage />} />
            <Route path="following" element={<Navigate to="/" replace />} />
            <Route path="tags" element={<TagsBrowse />} />
            <Route path="tags/:slug" element={<TagFeed />} />
            <Route path="privacy" element={<PrivacyPolicy />} />
            <Route path="terms" element={<TermsOfService />} />
            <Route path="discover" element={<Navigate to="/" replace />} />

            {/* Protected public routes */}
            <Route
              path="saved"
              element={
                <ProtectedRoute>
                  <SavedReviews />
                </ProtectedRoute>
              }
            />
            <Route
              path="reviews/create"
              element={
                <ProtectedRoute>
                  <CreateReview />
                </ProtectedRoute>
              }
            />
            <Route
              path="reviews/:id/edit"
              element={
                <ProtectedRoute>
                  <EditReview />
                </ProtectedRoute>
              }
            />
            <Route
              path="notifications"
              element={
                <ProtectedRoute>
                  <Notifications />
                </ProtectedRoute>
              }
            />
            <Route
              path="settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />
            <Route
              path="settings/password"
              element={
                <ProtectedRoute>
                  <ChangePassword />
                </ProtectedRoute>
              }
            />
            <Route
              path="settings/delete-account"
              element={
                <ProtectedRoute>
                  <DeleteAccount />
                </ProtectedRoute>
              }
            />
            <Route
              path="settings/blocked"
              element={
                <ProtectedRoute>
                  <BlockedUsers />
                </ProtectedRoute>
              }
            />
          </Route>

          {/* Admin routes with admin layout */}
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminLayout />
              </AdminRoute>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="products" element={<AdminProducts />} />
            <Route path="products/:id" element={<AdminManageProduct />} />
            <Route path="reviews" element={<AdminReviews />} />
            <Route path="moderation" element={<AdminModeration />} />
            <Route path="categories" element={<AdminCategories />} />
            <Route path="import" element={<AdminImportProducts />} />
            <Route path="seed" element={<AdminSeed />} />
          </Route>

          {/* Catch-all redirect */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}


export default App;
